<script setup lang="ts">
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useLayout } from '@/layout/composables/layout';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AppMenu from './AppMenu.vue';
import AppMenuProfile from './AppMenuProfile.vue';

const BREAKPOINT = 992;
const mediaQuery = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);
const { layoutConfig, layoutState, isDesktop, isHorizontal, isSlim, isSlimPlus, hasOpenOverlay, hasOverlaySubmenu, hasOpenOverlaySubmenu } = useLayout();
const route = useRoute();
const auth = useAuthStore();
const menuContainerRef = ref(null);
const sidebarRef = ref(null);

// Logo: usa il logo aziendale se disponibile, altrimenti il default Immigreer.
// Stessa logica del topbar (cache-buster per azienda su URL fisso /api/v2/aziende/logo).
const logoUrl = computed(() => {
    const url = auth.me?.logoUrl;
    if (!url) return '/images/immigreer-logo.png';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${auth.me?.aziendaId ?? ''}`;
});
const logoError = ref(false);
watch(logoUrl, () => { logoError.value = false; });

let timeout = null;
let observer = null;
let outsideClickListener = null;

watch(
    () => route.path,
    (newPath) => {
        if (hasOverlaySubmenu.value && isDesktop()) layoutState.activePath = null;
        else layoutState.activePath = newPath;

        layoutState.overlayMenuActive = false;
        layoutState.mobileMenuActive = false;
        layoutState.menuHoverActive = false;
    },
    { immediate: true }
);

watch(hasOpenOverlay, (newVal) => {
    if (isDesktop()) {
        if (newVal) bindOutsideClickListener();
        else unbindOutsideClickListener();
    }
});

watch(hasOpenOverlaySubmenu, (newValue) => {
    if (isDesktop()) {
        if (newValue) nextTick(setupIntersectionObserver);
        else unbindObserver();
    }
});

const screenChangeListener = () => {
    if (hasOverlaySubmenu.value) {
        layoutState.activePath = isDesktop() ? null : route.path;
        layoutState.menuHoverActive = false;
        unbindOutsideClickListener();
        unbindObserver();
    }
};

const bindOutsideClickListener = () => {
    if (!outsideClickListener) {
        outsideClickListener = (event) => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;

                if (hasOverlaySubmenu.value) {
                    layoutState.activePath = null;
                    layoutState.menuHoverActive = false;
                }
            }
        };

        document.addEventListener('click', outsideClickListener);
    }
};

const unbindOutsideClickListener = () => {
    if (outsideClickListener) {
        document.removeEventListener('click', outsideClickListener);
        outsideClickListener = null;
    }
};

const isOutsideClicked = (event) => {
    const topbarButtonEl = document.querySelector('.layout-menu-button');

    return !(sidebarRef.value.isSameNode(event.target) || sidebarRef.value.contains(event.target) || topbarButtonEl?.isSameNode(event.target) || topbarButtonEl?.contains(event.target));
};

const onMouseEnter = () => {
    if (!layoutState.anchored) {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        layoutState.sidebarExpanded = true;
    }
};

const onMouseLeave = () => {
    if (!layoutState.anchored && !timeout) {
        timeout = setTimeout(() => (layoutState.sidebarExpanded = false), 300);
    }
};

const onAnchorToggle = () => {
    layoutState.anchored = !layoutState.anchored;
};

const onMenuScroll = () => {
    if (menuContainerRef.value) {
        if (isHorizontal.value) {
            menuContainerRef.value.style.setProperty('--menu-scroll-x', `-${menuContainerRef.value.scrollLeft}px`);
        } else if (isSlim.value || isSlimPlus.value) {
            menuContainerRef.value.style.setProperty('--menu-scroll-y', `-${menuContainerRef.value.scrollTop}px`);
        }
    }

    if (hasOverlaySubmenu.value && isDesktop()) {
        layoutState.activePath = null;
    }
};

const setupIntersectionObserver = () => {
    if (!menuContainerRef.value) return;

    if (observer) {
        observer.disconnect();
    }

    const activeMenuItem = menuContainerRef.value.querySelector('.layout-root-menuitem.active-menuitem');
    if (!activeMenuItem) return;

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (isDesktop() && !entry.isIntersecting && hasOverlaySubmenu.value && layoutState.activePath) {
                    layoutState.activePath = null;
                }
            });
        },
        {
            root: menuContainerRef.value,
            threshold: 0
        }
    );

    observer.observe(activeMenuItem);
};

const unbindObserver = () => {
    if (observer) {
        observer.disconnect();
        observer = null;
    }
};

mediaQuery.addEventListener('change', screenChangeListener);

onBeforeUnmount(() => {
    unbindOutsideClickListener();
    unbindObserver();
    mediaQuery.removeEventListener('change', screenChangeListener);
});
</script>

<template>
  <div
    ref="sidebarRef"
    class="layout-sidebar"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="layout-sidebar-top">
      <a href="/">
        <img
          v-if="!logoError"
          :src="logoUrl"
          alt="Immigreer"
          class="layout-sidebar-logo"
          style="max-height: 32px; max-width: 160px; object-fit: contain;"
          @error="logoError = true"
        >
        <span
          v-else
          class="layout-sidebar-logo font-bold text-xl"
        >Immigreer</span>
        <span class="layout-sidebar-logo-slim font-bold text-xl">I</span>
      </a>
      <button
        class="layout-sidebar-anchor"
        type="button"
        @click="onAnchorToggle"
      />
    </div>
    <AppMenuProfile v-if="layoutConfig.menuProfilePosition === 'start'" />
    <div
      ref="menuContainerRef"
      class="layout-menu-container"
      @scroll="onMenuScroll"
    >
      <AppMenu />
    </div>
    <AppMenuProfile v-if="layoutConfig.menuProfilePosition === 'end'" />
  </div>
</template>

<style scoped>
/* Stessa ragione della topbar (vedi AppTopbar.vue): il logo — il nostro e quello
   dell'azienda — è un'immagine a colori con lettering scuro, e 11 dei 12 temi di menu
   hanno la sidebar scura. Piastrina chiara dietro al logo, tranne sul tema chiaro. */
.layout-wrapper:not(.layout-menu-light) .layout-sidebar-top > a {
  padding: 0.25rem 0.5rem;
  background: #fff;
  border-radius: 9px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
